import { useCallback, useEffect, useMemo } from "react";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";

import {
  useCreateTranscriptionSession,
  useGetEncounterById,
  useGetEncounterTranscriptLastOffset,
  useGetPatientById,
} from "@elation/api-client/src/stitch-web";
import type { ClientNoteTranscriptItem } from "@elation/transcribe";

import {
  type AIScribeProps,
  PatientLayout,
} from "@src/deprecated/pages/PatientLayout";
import { matchesActiveEncounter, useEncounter } from "@src/features/encounters";
import {
  useIsInElationEHR,
  usePatientTranscriptionRedirect,
} from "@src/features/iframe";
import { usePatientId } from "@src/features/patients";
import {
  useAppBusEvent,
  useAppSSEEventsFallbackPolling,
} from "@src/features/sse";
import { navigation } from "@src/shared/navigation";
import { useActor } from "@src/shared/providers/actorProvider";
import {
  NUMBER_ID_NOT_READY,
  STRING_ID_NOT_READY,
} from "@src/shared/utils/constants";
import { events, parseSource } from "@src/shared/utils/observability/events";

/**
 * Layout for patient-scoped routes. Syncs the URL :patientId param to the app store
 * via setPatientId so child routes and consumers can use usePatientId/useCurrentPatient.
 * Clears patient id when leaving patient routes.
 */
export default function PatientLayoutPage() {
  const params = useParams();
  const patientId = params.patientId;
  const navigate = useNavigate();
  const location = useLocation();
  const [, setPatientId] = usePatientId();

  const isInElationEHR = useIsInElationEHR();

  useEffect(() => {
    const numericPatientId = patientId ? Number(patientId) : null;

    if (!numericPatientId || !Number.isFinite(numericPatientId)) {
      setPatientId(null);
      return;
    }

    setPatientId(numericPatientId);

    return () => {
      setPatientId(null);
    };
  }, [patientId, setPatientId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: location.state is intentionally read by closure and excluded — we fire only once per patientId change, not on intra-patient navigations that mutate location.state.
  useEffect(() => {
    if (patientId == null) return;
    events.openPatient.track({
      patient_id: Number(patientId),
      source: parseSource(location.state),
    });
  }, [patientId]);

  const { data: patientData } = useGetPatientById(Number(patientId), {
    query: {
      enabled: !!patientId,
    },
  });

  const {
    mutateAsync: createTranscriptionSession,
    data: createTranscriptionSessionData,
  } = useCreateTranscriptionSession();

  const actor = useActor();

  const { encounterId, isEncounterCompleted, isEncounterFinalizing } =
    useEncounter();

  const { isFallbackPolling } = useAppSSEEventsFallbackPolling();

  const encounterQuery = useGetEncounterById(
    encounterId ?? NUMBER_ID_NOT_READY,
    {
      query: {
        enabled: encounterId != null,
      },
    },
  );

  const encounter = encounterQuery.data?.data;

  const getScribingSessionId = useCallback(async () => {
    if (isInElationEHR) {
      throw new Error("Scribing session is not supported in iframe mode");
    }

    if (createTranscriptionSessionData?.data) {
      return createTranscriptionSessionData?.data.scribingSession.id;
    }

    throw new Error("Scribing session not found");
  }, [createTranscriptionSessionData?.data, isInElationEHR]);

  const scribingSessionId = useMemo(() => {
    if (isInElationEHR) {
      return null;
    }

    if (createTranscriptionSessionData?.data) {
      return createTranscriptionSessionData?.data.scribingSession.id;
    }

    return null;
  }, [createTranscriptionSessionData?.data, isInElationEHR]);

  const { data: lastOffsetData } = useGetEncounterTranscriptLastOffset(
    encounterId ?? NUMBER_ID_NOT_READY,
    scribingSessionId ?? STRING_ID_NOT_READY,
    {
      query: {
        enabled: encounterId != null && scribingSessionId != null,
        refetchOnMount: "always",
        staleTime: Infinity,
      },
    },
  );

  const saveTranscriptItem = useCallback(
    async (_input: {
      scribingSessionId: string;
      data: ClientNoteTranscriptItem;
    }): Promise<void> => {
      // no-op: persistence not yet wired up; satisfies SaveTranscriptItem contract
    },
    [],
  );

  const onStartVisit = useCallback(async () => {
    if (isInElationEHR) {
      throw new Error("Transcription is not supported in iframe mode");
    }

    const result = await createTranscriptionSession({
      data: {
        patientId: Number(patientId),
      },
    });

    return result.data.scribingSession.el8TranscribeSessionId;
  }, [patientId, createTranscriptionSession, isInElationEHR]);

  const onFinishVisit = useCallback(async () => {
    if (!encounterId) return;
    if (!patientId) return;

    navigate(navigation.patientEncounter(patientId, encounterId.toString()));
  }, [encounterId, patientId, navigate]);

  const websocketUrl = actor.config.el8TranscribeWebsocketUrl;

  const aiScribe = useMemo((): AIScribeProps => {
    return {
      websocketUrl,
      getScribingSessionId,
      saveTranscriptItem,
      isActiveEncounter: encounter
        ? encounter.status === "created" ||
          encounter.status === "in_progress" ||
          encounter.status === "finalizing"
        : false,
      encounterCompleted: isEncounterCompleted,
      encounterFinalizing: isEncounterFinalizing,
      hasSoapnote: Boolean(encounter?.note),
      onFinishVisit,
      onStartVisit,
      lastOffset: lastOffsetData?.data?.offset ?? 0,
      encounterId: encounterId ?? null,
    };
  }, [
    encounter,
    encounterId,
    getScribingSessionId,
    onFinishVisit,
    onStartVisit,
    saveTranscriptItem,
    websocketUrl,
    lastOffsetData?.data?.offset,
    isEncounterCompleted,
    isEncounterFinalizing,
  ]);

  usePatientTranscriptionRedirect({ patientId, actor });

  useAppBusEvent("encounter.completed", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    if (params.encounterId != null && patientId) {
      void navigate(
        navigation.patientEncounter(patientId, String(payload.encounterId)),
      );
    }
  });

  useAppBusEvent("encounter.superseded_by_reopen", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    if (params.encounterId != null && patientId) {
      void navigate(
        navigation.patientEncounter(patientId, String(payload.newEncounterId)),
      );
    }
  });

  if (!patientId) {
    return <Navigate to={navigation.dashboard()} />;
  }

  if (!patientData) {
    return <></>;
  }

  return (
    <PatientLayout patient={patientData.data} aiScribe={aiScribe}>
      <Outlet
        context={
          {
            patientId,
            isFallbackPolling,
          } satisfies PatientLayoutContext
        }
      />
    </PatientLayout>
  );
}

export interface PatientLayoutContext {
  patientId: string;
  isFallbackPolling: boolean;
}

export function usePatientLayoutContext(): Partial<PatientLayoutContext> {
  const result = useOutletContext<PatientLayoutContext>();

  return result ?? {};
}
